// Best-effort "does this device look like it has a camera and a gyroscope"
// check, used to decide whether to show the camera-based cut angle button
// at all — done without ever prompting for camera/motion permission, since
// asking just to decide whether to show a button would be backwards.
//
// There's no permission-free way to be certain a gyroscope exists: browsers
// only reveal that once you've registered a 'deviceorientation' listener and
// started receiving real (non-null) samples, and on iOS 13+ that itself
// requires a granted permission prompt. So this leans on signals that ARE
// available without asking:
//   - DeviceOrientationEvent.requestPermission existing as a function is
//     iOS-13+-only, and that API only ships on phones/tablets with the
//     underlying motion sensors — so its presence alone is a reliable "yes".
//   - Elsewhere (Android, desktop), we fall back to a touch-primary pointer
//     (real desktops/laptops are mouse-primary) plus the orientation API at
//     least existing in the engine. Not airtight (a touchscreen Windows
//     laptop with no gyro would still pass), but the component itself
//     already degrades gracefully — "no sensor reading" message, camera
//     permission errors, etc — for whatever slips through.
export function supportsCutAngleCamera() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const hasCamera = !!navigator.mediaDevices?.getUserMedia;
  if (!hasCamera) return false;

  const hasOrientationApi = typeof window.DeviceOrientationEvent !== 'undefined';
  if (!hasOrientationApi) return false;

  const iosPermissionGated = typeof window.DeviceOrientationEvent.requestPermission === 'function';
  if (iosPermissionGated) return true;

  const isTouchPrimary = window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  return isTouchPrimary;
}
